/** biome-ignore-all lint/correctness/useExhaustiveDependencies: apiUrlCreator changes on workspace switch */
import ArticleUpdaterService from "@components/Article/ArticleUpdater/ArticleUpdaterService";
import { useRouter } from "@core/Api/useRouter";
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import { useDeferApi } from "@core-ui/hooks/useApi";
import BranchUpdaterService from "@ext/git/actions/Branch/BranchUpdaterService/logic/BranchUpdaterService";
import OnBranchUpdateCaller from "@ext/git/actions/Branch/BranchUpdaterService/model/OnBranchUpdateCaller";
import tryOpenMergeConflict from "@ext/git/actions/MergeConflictHandler/logic/tryOpenMergeConflict";
import type MergeData from "@ext/git/actions/MergeConflictHandler/model/MergeData";
import { useCallback, useState } from "react";
import { useBranchCheckout } from "./useBranchCheckout";

type BranchActionsCallbacks = {
	onSwitched: () => Promise<void> | void;
	onCreated: () => Promise<void> | void;
};

export const useBranchActions = ({ onSwitched, onCreated }: BranchActionsCallbacks) => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const router = useRouter();
	const [isProcessing, setIsProcessing] = useState(false);
	const { checkoutBranch, checkoutDialog } = useBranchCheckout();
	const { call: getMergeData } = useDeferApi<MergeData>({ url: (api) => api.getMergeData() });

	const switchBranch = useCallback(
		async (branchName: string) => {
			setIsProcessing(true);
			try {
				const response = await checkoutBranch(branchName);
				if (!response?.ok) return;

				await router.pushPath(await response.text());
				await BranchUpdaterService.updateBranch(apiUrlCreator);
				await ArticleUpdaterService.update(apiUrlCreator);
				await onSwitched();

				tryOpenMergeConflict({ mergeData: await getMergeData() });
			} finally {
				setIsProcessing(false);
			}
		},
		[apiUrlCreator, checkoutBranch, getMergeData, onSwitched, router],
	);

	const createBranch = useCallback(
		async (branchName: string) => {
			setIsProcessing(true);
			try {
				const response = await FetchService.fetch(
					apiUrlCreator.getVersionControlCreateNewBranchUrl(branchName),
				);
				if (!response.ok) return;

				await BranchUpdaterService.updateBranch(apiUrlCreator, OnBranchUpdateCaller.CheckoutToNewCreatedBranch);
				await onCreated();
			} finally {
				setIsProcessing(false);
			}
		},
		[apiUrlCreator, onCreated],
	);

	return { switchBranch, createBranch, isProcessing, checkoutDialog };
};
